'use client';

import React, { useCallback, useEffect, useRef } from 'react';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  status?: 'error' | 'warning';
  variant?: 'outlined';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  onPressEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

function Input({ status, variant, prefix, suffix, onPressEnter, onKeyDown, className, ...rest }: InputProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onPressEnter?.(e);
    onKeyDown?.(e);
  };

  const hasWrapper = prefix || suffix;

  const inputEl = (
    <input
      className={['ui-input', variant, status, !hasWrapper && className].filter(Boolean).join(' ')}
      onKeyDown={handleKeyDown}
      {...rest}
    />
  );

  if (!hasWrapper) return inputEl;

  return (
    <span className={['ui-input-wrapper', variant, status, className].filter(Boolean).join(' ')}>
      {prefix && <span className="prefix">{prefix}</span>}
      {inputEl}
      {suffix && <span className="suffix">{suffix}</span>}
    </span>
  );
}

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  status?: 'error' | 'warning';
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  nonDraggable?: boolean;
}

function TextArea({ status, className, autoSize, nonDraggable, onChange, value, ...rest }: TextAreaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el || !autoSize) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoSize]);

  useEffect(() => {
    resize();
  }, [resize, value]);

  return (
    <textarea
      ref={ref}
      className={['ui-input', 'textarea', status, nonDraggable && 'non-draggable', className].filter(Boolean).join(' ')}
      style={autoSize ? { overflow: 'hidden', resize: 'none' } : undefined}
      value={value}
      onChange={(e) => {
        resize();
        onChange?.(e);
      }}
      {...rest}
    />
  );
}

Input.TextArea = TextArea;

export default Input;
